import React, { useState } from 'react';
import {
    Button,
    Typography,
    Box,
    Menu,
    MenuItem,
    IconButton,
    Tooltip
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import RefreshIcon from '@mui/icons-material/Refresh'; // New icon for reopening
import { useDrag } from 'react-dnd';

const STATUS_COLORS = {
    "No Good": "#D83B50",      // Red
    "Pretty Mid": "#00A9CC",   // Blue
    "Final Draft": "#3EA247",  // Green
    "Initial": "#F1C400"
};

const TaskButton = ({ content }) => {
    const [status, setStatus] = useState("Initial");
    const [anchorEl, setAnchorEl] = useState(null);

    // Controls whether the text is "open" or "closed"
    const [isClosed, setIsClosed] = useState(false);

    // For "Copy text" tooltip
    const [copyTooltip, setCopyTooltip] = useState("Copy");

    // Setup drag behavior
    const [{ isDragging }, dragRef] = useDrag({
        type: 'task',
        item: { content, status },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        end: (item, monitor) => {
            // If actually dropped, we close the text
            if (monitor.didDrop()) {
                setIsClosed(true);
            }
        },
    });

    /***** COPY TO CLIPBOARD ******/
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(content);
            setCopyTooltip("Copied!");
            setTimeout(() => setCopyTooltip("Copy"), 1000);
        } catch (err) {
            console.error("Failed to copy text:", err);
        }
    };

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        handleMenuClose();
    };

    // If the user chooses to "close" it manually
    const handleManualClose = () => {
        setIsClosed(true);
    };

    // If the user wants to "reopen" the closed button
    const handleReopen = () => {
        setIsClosed(false);
    };

    // If text is closed, show truncated; otherwise show full.
    const truncatedContent = content.length > 40
        ? content.slice(0, 40) + '...'
        : content;

    const displayText = isClosed ? truncatedContent : content;

    return (
        <Box
            ref={dragRef}
            display="flex"
            alignItems="center"
            mb={1}
            sx={{
                opacity: isDragging ? 0.5 : 1,
            }}
        >
            {/* MAIN BUTTON */}
            <Button
                variant="contained"
                fullWidth
                sx={{
                    bgcolor: STATUS_COLORS[status],
                    color: '#fff',
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    p: 1,
                    '&:hover': {
                        bgcolor: STATUS_COLORS[status],
                    },
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        fontSize: '14px',
                        color: 'black',
                        textAlign: 'left',
                        flexGrow: 1,
                        whiteSpace: 'pre-wrap', // preserve original formatting
                    }}
                >
                    {displayText}
                </Typography>
            </Button>

            {/* COPY TEXT BUTTON */}
            <Tooltip title={copyTooltip} arrow>
                <IconButton onClick={handleCopy} sx={{ ml: 1 }}>
                    <ContentCopyIcon />
                </IconButton>
            </Tooltip>

            {/* CLOSE TEXT BUTTON */}
            {!isClosed ? (
                <Tooltip title="Close text" arrow>
                    <IconButton onClick={handleManualClose} sx={{ ml: 1 }}>
                        <CloseFullscreenIcon />
                    </IconButton>
                </Tooltip>
            ) : (
                <Tooltip title="Reopen text" arrow>
                    <IconButton onClick={handleReopen} sx={{ ml: 1 }}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            )}

            {/* STATUS MENU ICON */}
            <IconButton onClick={handleMenuOpen}>
                <MoreVertIcon />
            </IconButton>

            {/* STATUS SELECTION MENU */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleStatusChange("No Good")}>No Good</MenuItem>
                <MenuItem onClick={() => handleStatusChange("Pretty Mid")}>Pretty Mid</MenuItem>
                <MenuItem onClick={() => handleStatusChange("Final Draft")}>Final Draft</MenuItem>
            </Menu>
        </Box>
    );
};

export default TaskButton;
